import { useEffect, useMemo, useState } from 'react';
import { 
  MantineReactTable, 
  useMantineReactTable,
  MRT_EditActionButtons 
} from 'mantine-react-table';
import { MRT_Localization_PT_BR } from 'mantine-react-table/locales/pt-BR';
import { apiInstance } from '../services/api_service';
import { Box, Text, Button, Flex, Stack, Title, Checkbox } from '@mantine/core';
import DataTableContainer from '../components/containers/DataTableContainer';
import { toast } from 'react-toastify';
import UserDetails from '../components/tables/UserDetails';

const Users = () => {
  //data and fetching state
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  // Validation errors state for create user
  const [validationErrors, setValidationErrors] = useState({});
  
  // Creating user state
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  //table state
  const [tableInstance, setTableInstance] = useState(null);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const fetchData = async () => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    if(tableInstance) {
      tableInstance.resetExpanded();
    }

    const params = {
      page_size: pagination.pageSize,
      page: pagination.pageIndex + 1,
    }
    columnFilters.forEach((filter, _) => {
      switch (filter.id) {
        case 'first_name':
          params[`first_name__icontains`] = filter.value;
          break;
        case 'last_name':
          params[`last_name__icontains`] = filter.value;
          break;
        case 'email':
          params[`email__icontains`] = filter.value;
          break;
        default:
          break;
      }
    });
    if (globalFilter) {
      params[`search`] = globalFilter;
    } 
    const queryString = new URLSearchParams(params).toString();
    try {
      const response = await apiInstance.get(`authentication/users/?${queryString}`);
      setData(response.data.results);
      setRowCount(response.data.count);
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }
    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnFilters, //refetch when column filters change
    globalFilter, //refetch when global filter changes
    pagination.pageIndex, //refetch when page index changes
    pagination.pageSize, //refetch when page size changes
    sorting, //refetch when sorting changes
  ]);

      const createUser = async (userInfo) => {
      setIsCreatingUser(true);
      try {
        const response = await apiInstance.post('authentication/users/', userInfo);
        console.log('User created:', response.data);
        fetchData();
        toast.success(<div><Text>{"Usuário criado com sucesso"}</Text></div>, {
          position: "top-right",
        });
      } catch (error) {
        console.error('error creating user:', error);
        toast.error(<div><Text>{"Houve um erro ao criar o usuário"}</Text><Text>{"Tente novamente"}</Text></div>, {
          position: "top-right",
        });
      } finally {
        setIsCreatingUser(false);
      }
    };

  const handleCreateUser = async ({ values, exitCreatingMode }) => {
    const newValidationErrors = validateUser(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});
    await createUser(values);
    exitCreatingMode();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'profile.external_image_link',
        header: 'Foto',
        size: 25,
        maxSize: 25,
        enableEditing: false,
        enableFilters: false,
        enableColumnFilters: false,
        enableSorting: false,
        Cell: ({ renderedCellValue, row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            { renderedCellValue ? (
              <img className='relative w-10 h-10 rounded-full' src={renderedCellValue} alt='Rounded Avatar'></img> 
              ) : (
              <div className='relative w-10 h-10 bg-gray-100 flex justify-center items-center rounded-full'>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M15.9998 7C15.9998 9.20914 14.2089 11 11.9998 11C9.79067 11 7.99981 9.20914 7.99981 7C7.99981 4.79086 9.79067 3 11.9998 3C14.2089 3 15.9998 4.79086 15.9998 7Z" stroke="#4B5563" strokeWidth="1.6" />
                <path d="M11.9998 14C9.15153 14 6.65091 15.3024 5.23341 17.2638C4.48341 18.3016 4.10841 18.8204 4.6654 19.9102C5.2224 21 6.1482 21 7.99981 21H15.9998C17.8514 21 18.7772 21 19.3342 19.9102C19.8912 18.8204 19.5162 18.3016 18.7662 17.2638C17.3487 15.3024 14.8481 14 11.9998 14Z" stroke="#4B5563" strokeWidth="1.6" />
                </svg>
              </div>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 80,
        maxSize: 80,
        enableSorting: false,
        mantineEditTextInputProps: {
          type: 'email',
          required: true,
          error: validationErrors?.email,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              email: undefined,
            }),
        },
      },
      {
        accessorKey: 'first_name',
        header: 'Nome',
        size: 80,
        maxSize: 80,
        mantineEditTextInputProps: {
          type: 'text',
          error: validationErrors?.first_name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              first_name: undefined,
            }),
        },
      },
      {
        accessorKey: 'last_name',
        header: 'Sobrenome',
        size: 80,
        maxSize: 80,
        mantineEditTextInputProps: {
          type: 'text',
          error: validationErrors?.last_name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              last_name: undefined,
            }),
        },
      },
      {
        accessorKey: 'is_superuser',
        header: 'Administrador',
        size: 40,
        maxSize: 40,
        enableEditing: false,
        enableFilters: false,
        enableColumnFilters: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              checked={cell.getValue()}
              readOnly
              size="sm"
              color="blue"
            />
          </Box>
        ),
      },
      {
        accessorKey: 'is_active',
        header: 'Ativo',
        size: 40,
        maxSize: 40,
        enableEditing: false,
        enableFilters: false,
        enableColumnFilters: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              checked={cell.getValue()}
              readOnly
              size="sm"
              color="green"
            />
          </Box>
        ),
      },
    ],
    [validationErrors],
  );

  const table = useMantineReactTable({
    renderTopToolbarCustomActions: ({ table }) => {
      setTableInstance(table);
      return (
        <Flex align="center" gap="md">
          <Button variant="outline" color="lime" radius="md" size="xs"
            onClick={() => {
              table.setCreatingRow(true);
            }}
          >
            Criar usuário
          </Button>
        </Flex>
      );
    },
    columns,
    data,
    createDisplayMode: 'modal',
    onCreatingRowSave: handleCreateUser,
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => (
      <Stack>
        <Title order={3}>Criar usuário</Title>
        {internalEditComponents}
        <Flex justify="flex-end" mt="xl">
          {/* eslint-disable-next-line */}
          <MRT_EditActionButtons variant="text" table={table} row={row} />
        </Flex>
      </Stack>
    ),
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowSelection: false,
    enableColumnActions: false,
    layoutMode: 'grid',
    getRowId: (row) => row.id,
    initialState: {
      density: 'xs', 
      showColumnFilters: false,
    },
    manualPagination: true,
    enableSorting: false,
    manualSorting: true,
    enableFilterMatchHighlighting: true,
    localization: MRT_Localization_PT_BR,
    // Density
    enableDensityToggle: false,
    // Search
    enableGlobalFilter: true,
    // Filter
    manualFiltering: true,
    columnFilterDisplayMode: 'subheader',
    rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    autoResetPageIndex: false,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      isSaving: isCreatingUser,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: '',
        size: 5,
        maxSize: 5,
      },
      'mrt-row-actions': {
        // header: '...',
        size: 10,
        maxSize: 10,
      },
    },
    mantineFilterTextInputProps: {
      disabled: false,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: 'red', children: 'Falha ao carregar dados' }
      : undefined,
    mantinePaperProps: {
      sx: {
        height: '100%',
        border: '0px solid !important',
        boxShadow: '0px 0px 0px 0px !important',
      }
    },
    mantineTableProps: {
      sx: {
        // Force detail panel to use full width
        '& tbody tr[data-expanded="true"] + tr td': {
          width: '100% !important',
          maxWidth: '100% !important',
          padding: '0 !important',
        },
        '& tbody tr[data-expanded="true"] + tr': {
          width: '100% !important',
        },
        // Override any width restrictions on detail panel cells
        '& .mantine-Table-td[data-column="mrt-row-expand"]': {
          width: '100% !important',
          maxWidth: '100% !important',
        }
      }
    },
    mantineTopToolbarProps: {
      sx: {
        
      }
    },
    mantineTableHeadProps: {
      sx: {
        
      }
    },
    mantineTableContainerProps: ({table}) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      }
    }),
    mantineTableBodyCellProps: ({ row }) => {
      return {
        sx: {
          backgroundColor: "#ffffff",
        },
      }
    },
    mantineTableBodyRowProps: ({ row }) => ({
      sx: {
        "@media (hover: hover)": {
          "[data-hover] > tbody > tr:hover": {
            backgroundColor: "#fff !important",
          },
        },
        // Override detail panel restrictions
        '& td[data-index="mrt-row-expand"]': {
          width: '100% !important',
          maxWidth: '100% !important',
        },
      }
    }),
    mantineDetailPanelProps: {
      sx: {
        width: '100% !important',
        maxWidth: '100% !important',
        border: '0px !important',
        '& > div': {
          width: '100% !important',
          maxWidth: '100% !important',
        }
      }
    },
    // Actions
    enableEditing: false,
    enableRowActions: false,
    positionActionsColumn: 'last',
    renderRowActionMenuItems: ({ row }) => {
      // return [{label: 'Editar'}]
      return []
    },

    // Row detail
    enableExpandAll: false,
    renderDetailPanel: ({ row }) => {
      let isExpanded = row.getIsExpanded()
      if (!isExpanded) {
        return null;
      }
      return (
        <UserDetails userId={row.original.id} />
      )
    },
  });

  return (
    <DataTableContainer>
      <div style={{height: '93vh'}}>
        <MantineReactTable table={table} style={{border: '0px'}} />
      </div>
    </DataTableContainer>
  );
};

// Validation functions
const validateEmail = (email) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

function validateUser(user) {
  return {
    email: !validateEmail(user.email) ? 'Email é obrigatório e deve ter formato válido' : '',
    first_name: '', // Nome is optional
    last_name: '', // Sobrenome is optional
  };
}

export default Users;
